/**
 * speciesSize.ts — a Pokémon's size against its own species (2026-09-16).
 *
 * GO's size badges are relative: XXS is under half the species' average
 * height, XXL is one and a half times it or more, and everything the game
 * says about a Pokémon's size (the badge, the Showcase score, the record
 * book) starts from that ratio. The averages come from the Game Master
 * (speciesSize.generated.ts, per species, regional form and Mega).
 *
 * Badge boundaries (Bulbapedia, GO Hub DB, checked against Squirtle /
 * Houndoom / Snorlax / Wailord / Joltik on 2026-09-16):
 *   XXS  0.49x to 0.50x      XS  0.50x to 0.75x
 *   M    0.75x to 1.25x      XL  1.25x to 1.50x
 *   XXL  1.50x up to the species' own ceiling (1.55x, 1.75x or 2.00x).
 */
import { SPECIES_SIZE } from "./generated/speciesSize.generated";
import type { MyPokemonRecord, MyPokemonSizeClass } from "./myPokemonStore";

export interface SpeciesAverageSize {
  heightM: number;
  weightKg: number;
}

/** A formId slug's regional token, matching the generated table's keys. */
function regionalKeyFor(formId: string | null | undefined): string | null {
  if (!formId) return null;
  const f = formId.toLowerCase();
  if (f.includes("alola")) return "ALOLA";
  if (f.includes("galar")) return "GALARIAN";
  if (f.includes("hisui")) return "HISUIAN";
  if (f.includes("paldea")) return "PALDEA";
  return null;
}

/**
 * The species' average height and weight, for the given form. A regional
 * form has its own body; every other form shares the base row. `mega` asks
 * for the Mega Evolution's averages ("" / "X" / "Y" form label) where the
 * species has one, falling back to the base body.
 */
export function speciesAverageSize(
  speciesId: number | null | undefined,
  formId?: string | null,
  mega?: "" | "X" | "Y" | null,
): SpeciesAverageSize | null {
  if (speciesId == null || !Number.isFinite(speciesId)) return null;
  const keys: string[] = [];
  if (mega != null) keys.push(`${speciesId}:MEGA${mega ? `_${mega}` : ""}`);
  const regional = regionalKeyFor(formId);
  if (regional) keys.push(`${speciesId}:${regional}`);
  keys.push(`${speciesId}`);
  for (const k of keys) {
    const row = SPECIES_SIZE[k];
    if (row) return { heightM: row[0], weightKg: row[1] };
  }
  return null;
}

/** This record's height as a multiple of its species' average, or null. */
export function heightRatioFor(
  r: Pick<MyPokemonRecord, "speciesId" | "formId" | "heightM">,
): number | null {
  if (r.heightM == null || !(r.heightM > 0)) return null;
  const avg = speciesAverageSize(r.speciesId, r.formId);
  if (!avg) return null;
  return r.heightM / avg.heightM;
}

/** This record's weight as a multiple of its species' average, or null. */
export function weightRatioFor(
  r: Pick<MyPokemonRecord, "speciesId" | "formId" | "weightKg">,
): number | null {
  if (r.weightKg == null || !(r.weightKg > 0)) return null;
  const avg = speciesAverageSize(r.speciesId, r.formId);
  if (!avg) return null;
  return r.weightKg / avg.weightKg;
}

/** The badge GO prints for a height ratio; null for the unbadged middle. */
export function sizeClassForRatio(ratio: number): MyPokemonSizeClass {
  if (ratio < 0.5) return "xxs";
  if (ratio < 0.75) return "xs";
  if (ratio < 1.25) return null;
  if (ratio < 1.5) return "xl";
  return "xxl";
}

/**
 * A height or weight the game could never print for this species. The
 * tallest any Pokémon gets is 2.0x its average and the shortest 0.25x
 * (Scatterbug's XXS floor); weight runs to about 2.75x. Anything beyond is
 * an OCR mangle ("2.36m" landing as 36 m), never a Pokémon.
 */
export const HEIGHT_RATIO_MIN = 0.2;
export const HEIGHT_RATIO_MAX = 2.2;
export const WEIGHT_RATIO_MIN = 0.1;
export const WEIGHT_RATIO_MAX = 3.5;

export function isPlausibleHeight(speciesId: number | null | undefined, formId: string | null | undefined, heightM: number): boolean {
  if (!(heightM > 0)) return false;
  const avg = speciesAverageSize(speciesId, formId);
  if (!avg) return true; // unknown species: nothing to judge against
  const ratio = heightM / avg.heightM;
  return ratio >= HEIGHT_RATIO_MIN && ratio <= HEIGHT_RATIO_MAX;
}

export function isPlausibleWeight(speciesId: number | null | undefined, formId: string | null | undefined, weightKg: number): boolean {
  if (!(weightKg > 0)) return false;
  const avg = speciesAverageSize(speciesId, formId);
  if (!avg) return true;
  const ratio = weightKg / avg.weightKg;
  return ratio >= WEIGHT_RATIO_MIN && ratio <= WEIGHT_RATIO_MAX;
}
